import { DialogContent } from "@mui/material";
import React, { useEffect, useState } from "react";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import CustomDropzone from "src/components/Form/Dropzone/Dropzone";
import Icon, { IconName } from "src/components/Icon/Icon";
import * as S from "src/constants/StringConstants";
import { IDocument } from "src/store/models/document.model";
import "./ContractingDocumentsModal.scss";

interface ContractingDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (filesToUpload: File[]) => void;
  existingDocuments: IDocument[];
  onDownload: (documentId: number) => void;
  onDelete: (documentIds: number[]) => void;
}

export const ContractingDocumentsModal: React.FC<ContractingDocumentsModalProps> = ({
  open,
  onClose,
  onSave,
  existingDocuments,
  onDownload,
  onDelete,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const handleFileDrop = (acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles]);
  };

  const toggleSelection = (id: number) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;

    const confirm = window.confirm(S.BRO_CONTRACTING_DOC_MODAL_DELETE_CONFIRMATION);
    if (confirm) {
      onDelete(selectedIds);
      setSelectedIds([]);
    }
  };

  const handleSubmit = async () => {
    await onSave(files);
    setFiles([]);
    setSelectedIds([]);
  };

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setSelectedIds([]);
    }
  }, [open]);

  return (
    <PromptModal
      width="60rem"
      bgColor="var(--app-color-secondary5)"
      open={open}
      title={S.BRO_CONTRACTING_DOC_MODAL_TITLE}
      confirmText={S.UDM_UPLOAD_BUTTON}
      rejectText={S.UDM_CANCEL_BUTTON}
      onConfirm={handleSubmit}
      onReject={onClose}>
      <DialogContent className="contracting-docs-modal">
        <section className="contracting-docs-modal__scrollable-docs-container">
          <div className="contracting-docs-modal__row-container">
            <div className="contracting-docs-modal__row-container">
              <div>
                {existingDocuments.length > 0 ? (
                  <div className="contracting-docs-modal__row-container">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === existingDocuments.length}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedIds(existingDocuments.map(doc => doc.documentID));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                    <h5 className="contracting-docs-modal__section-title">
                      {S.BRO_CONTRACTING_DOC_MODAL_FILE_NAME_LABEL}
                    </h5>
                  </div>
                ) : (
                  <p className="contracting-docs-modal__section-title">{S.BRO_CONTRACTING_DOC_MODAL_NO_FILES}</p>
                )}
              </div>
            </div>

            {selectedIds.length > 0 && (
              <button className="file-card__remove-btn" onClick={handleDeleteSelected}>
                <Icon name={IconName.DeleteFileGold} size="24px" />
              </button>
            )}
          </div>
          <hr />

          {existingDocuments.length > 0 && (
            <>
              {existingDocuments.map(doc => (
                <React.Fragment key={doc.documentID}>
                  <div className="contracting-docs-modal__row-container">
                    <div className="contracting-docs-modal__row-container">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(doc.documentID)}
                        onChange={() => toggleSelection(doc.documentID)}
                        onClick={e => e.stopPropagation()}
                      />
                      <a onClick={() => onDownload(doc.documentID)}>{doc.fileName}</a>
                    </div>
                  </div>
                  <hr />
                </React.Fragment>
              ))}
            </>
          )}
        </section>

        <section>
          <h5 className="contracting-docs-modal__section-title">{S.BRO_CONTRACTING_DOC_MODAL_DROPZONE_LABEL}</h5>

          <CustomDropzone
            files={files}
            onDrop={handleFileDrop}
            onRemove={idx => setFiles(prev => prev.filter((_, i) => i !== idx))}
          />
        </section>
      </DialogContent>
    </PromptModal>
  );
};
